import { AxiosHeaders, AxiosResponse } from 'axios';
import { axiosAuthInstance, axiosInstance } from '../../instance.ts';
import {
  IAuthResponse,
  IForgotConfirmResponse,
  IInviteValidationRequest,
  IResetPasswordRequest,
  ISignInRequest,
  ISignInResponse,
  ISignOutRequest,
  ISignUpRecruiterRequest,
  ISignUpRequest,
} from './types.ts';

export enum AuthAPIPaths {
  SIGN_UP_MANAGER = 'auth/sign-up-manager/',
  SIGN_IN = 'auth/sign-in/',
  SIGN_OUT = 'auth/sign-out/',
  FORGOT_PASSWORD = 'auth/forgot-password/',
  FORGOT_PASSWORD_RESEND = 'auth/resend-forgot-password/',
}

const AuthAPI = {
  signUpRecruiter: (
    payload: ISignUpRecruiterRequest,
  ): Promise<AxiosResponse<IAuthResponse, ISignUpRecruiterRequest>> =>
    axiosInstance.post('/auth/sign-up-recruiter/', payload),

  signUpManager: ({
    headers,
    ...payload
  }: ISignUpRequest): Promise<AxiosResponse<IAuthResponse, ISignUpRequest>> =>
    axiosInstance.post(AuthAPIPaths.SIGN_UP_MANAGER, payload, { headers }),

  signIn: ({
    headers,
    ...payload
  }: ISignInRequest): Promise<AxiosResponse<ISignInResponse, ISignInRequest>> =>
    axiosInstance.post(AuthAPIPaths.SIGN_IN, payload, { headers }),

  signOut: (payload: ISignOutRequest): Promise<AxiosResponse<void, ISignOutRequest>> =>
    axiosAuthInstance.post(AuthAPIPaths.SIGN_OUT, payload),

  forgotPassword: ({
    email,
    headers,
  }: {
    email: string;
    headers: AxiosHeaders;
  }): Promise<AxiosResponse<void, { email: string }>> =>
    axiosInstance.post(AuthAPIPaths.FORGOT_PASSWORD, { email, platform: 'WEB' }, { headers }),

  forgotPasswordResend: (email: string): Promise<AxiosResponse<void, { email: string }>> =>
    axiosInstance.post(AuthAPIPaths.FORGOT_PASSWORD_RESEND, { email, platform: 'WEB' }),

  forgotPasswordConfirmEmail: ({
    email,
    otc,
  }: {
    otc: string;
    email: string;
  }): Promise<AxiosResponse<IForgotConfirmResponse, void>> =>
    axiosInstance.post(`auth/forgot-password-confirm/?otc=${otc}&email=${encodeURIComponent(email)}&platform=WEB`),

  forgotPasswordSetNew: ({
    newPassword,
    token,
  }: IResetPasswordRequest): Promise<AxiosResponse<void, { newPassword: string }>> =>
    axiosInstance.post(
      'auth/forgot-password-reset/',
      { newPassword },
      { headers: { Authorization: `Bearer ${token}` } },
    ),

  confirmEmail: (otc: string): Promise<AxiosResponse<void, void>> =>
    axiosAuthInstance.post(`auth/confirm-email/?otc=${otc}`),

  resendConfirmEmailCode: (): Promise<AxiosResponse<void, void>> =>
    axiosAuthInstance.post('auth/resend-email-confirm/'),

  validateInviteToken: (token: string): Promise<AxiosResponse<void, IInviteValidationRequest>> =>
    axiosAuthInstance.post('/auth/validate-invite-token/', { token }),

  invalidateInviteToken: (token: string): Promise<AxiosResponse<void, IInviteValidationRequest>> =>
    axiosAuthInstance.post('/auth/invalidate-invite-token/', { token }),
};

export default AuthAPI;
